import React from 'react';
import { getLinkFragment } from '../../../lib/link-fragment/link-fragment';
import { StoryblokCommon } from '../../../lib/storyblok/storyblok';
import {
    NavigationItemTopStoryblok,
    NavigationItemOneStoryblok,
    NavigationItemTwoStoryblok,
    NavigationLinkStoryblok,
    NavigationSubLinkStoryblok,
} from '../../../_shared/interfaces/storyblok';

import * as StylesHs from './navigation-item.hs.module.scss';
import * as StylesDr from './navigation-item.dr.module.scss';

const NavigationItem = ({
    blok,
    storyblokCommon,
    activeNavigationItem,
    showLinksOnly = false,
    navigationId = 'navigation',
    onNavigationItemChange,
}: {
    blok: NavigationItemTopStoryblok | NavigationItemOneStoryblok | NavigationItemTwoStoryblok | undefined;
    storyblokCommon: StoryblokCommon;
    activeNavigationItem?: string;
    showLinksOnly?: boolean;
    navigationId?: string;
    onNavigationItemChange?: (navigationItem: NavigationItemTopStoryblok | NavigationItemOneStoryblok | NavigationItemTwoStoryblok) => any;
}) => {
    const styles = storyblokCommon.configuration.content.theme === 'dr' ? StylesDr : StylesHs;
    const [activeLink, setActiveLink] = React.useState<string | undefined>(undefined);

    const getNavigationItemStyle = (): string => {
        if (!blok) {
            return '';
        }

        switch (blok.component) {
            case 'navigation_item_top':
                return showLinksOnly ? styles.navigationItemLinks : styles.navigationItemTop;
            case 'navigation_item_one':
                return styles.navigationItemOne;
            case 'navigation_item_two':
                return styles.navigationItemTwo;
        }
    };

    const backToNavigationItem = () => {
        if (activeLink && blok?.component === 'navigation_item_top') {
            setActiveLink(undefined);
        } else if (onNavigationItemChange && blok) {
            onNavigationItemChange(blok);
        }
    };

    React.useEffect(() => {
        if (blok?.component === 'navigation_item_top' && activeLink) {
            setActiveLink(blok._uid);
        }
    }, [blok]);

    return (
        <>
            {blok && (
                <div id={navigationId + '-item-wrapper-' + blok._uid} className={styles.navigationItemWrapper}>
                    <div id={navigationId + '-item-' + blok._uid} className={[styles.navigationItem, getNavigationItemStyle()].join(' ')}>
                        <div id={navigationId + '-headline-' + blok._uid} className={styles.navigationItemHeadline}>
                            <button
                                role="menuitem"
                                id={navigationId + '-button-' + blok._uid}
                                aria-controls={navigationId + '-' + blok._uid}
                                onClick={backToNavigationItem}
                                className={styles.mobileHeadline}
                            >
                                {' '}
                                {(activeLink &&
                                    blok.navigation_links &&
                                    blok.navigation_links.find((link: NavigationLinkStoryblok) => link._uid === activeLink)?.text) ||
                                    (activeLink === blok._uid &&
                                        blok.component !== 'navigation_item_one' &&
                                        blok.navigation_items_headline) ||
                                    blok.text}
                                <span className={styles.desktopHeadline} onClick={backToNavigationItem}>
                                    {blok.navigation_items_headline}
                                </span>
                            </button>
                            <button
                                role="menuitem"
                                id={navigationId + '-button-' + blok._uid}
                                aria-controls={navigationId + '-' + blok._uid}
                                onClick={backToNavigationItem}
                                className={styles.desktopHeadline}
                            >
                                {blok.navigation_items_headline}
                            </button>
                        </div>
                        {showLinksOnly &&
                            blok.navigation_links &&
                            blok.navigation_links.map((link: NavigationLinkStoryblok) => {
                                return (
                                    <div
                                        key={link._uid + '-link-only'}
                                        id={navigationId + '-value-' + link._uid}
                                        role="none"
                                        className={!showLinksOnly ? styles.mobileLinks : ''}
                                    >
                                        <div className={styles.navigationLinkHeadline}>
                                            <p>{link.text}</p>
                                        </div>
                                        <div id={navigationId + '-links-' + link._uid} className={styles.gridItemOptions}>
                                            {link.links &&
                                                link.links.map((subLink: NavigationSubLinkStoryblok) => {
                                                    return <div key={subLink._uid + '-sublink-only'}>{getLinkFragment(subLink)}</div>;
                                                })}
                                        </div>
                                    </div>
                                );
                            })}

                        {!showLinksOnly &&
                            !activeLink &&
                            blok.navigation_links &&
                            blok.navigation_links.map((link: NavigationLinkStoryblok) => {
                                return (
                                    <div
                                        key={link._uid + '-prerender'}
                                        id={navigationId + '-value-' + link._uid}
                                        role="none"
                                        className={!showLinksOnly ? styles.mobileLinks : ''}
                                    >
                                        <button
                                            role="menuitem"
                                            id={navigationId + '-button-' + link._uid}
                                            className={activeNavigationItem === link._uid ? styles.navigationItemActive : ''}
                                            aria-haspopup="true"
                                            aria-controls={navigationId + '-' + link._uid}
                                            onClick={() => setActiveLink(activeLink === link._uid ? undefined : link._uid)}
                                        >
                                            {link.text}
                                        </button>
                                    </div>
                                );
                            })}

                        {!activeLink && (
                            <div
                                key={'navigation-items-' + blok._uid}
                                id={navigationId + '-navigation-items-' + blok._uid}
                                role="none"
                                className={styles.productHeadline}
                            >
                                <button
                                    role="menuitem"
                                    id={navigationId + '-navigation-items-button-' + blok._uid}
                                    aria-haspopup="true"
                                    aria-controls={navigationId + 'navigation-items-' + blok._uid}
                                    onClick={() => setActiveLink(blok._uid)}
                                >
                                    {blok.navigation_items_headline}
                                </button>
                            </div>
                        )}
                        {activeLink &&
                            blok.navigation_links &&
                            blok.navigation_links
                                .find((link: NavigationLinkStoryblok) => link._uid === activeLink)
                                ?.links?.map((subLink: NavigationSubLinkStoryblok) => {
                                    return (
                                        <div
                                            key={subLink._uid + '-sublink-active'}
                                            id={navigationId + '-value-' + subLink._uid}
                                            role="none"
                                            className={styles.navigationLinks}
                                        >
                                            {getLinkFragment(subLink)}
                                        </div>
                                    );
                                })}
                        {!showLinksOnly &&
                            blok.navigation_items.map((item) => {
                                return (
                                    <div
                                        key={item._uid + '-item-active'}
                                        id={navigationId + '-value-' + item._uid}
                                        role="none"
                                        className={activeLink !== blok._uid ? styles.navigationItems : ''}
                                    >
                                        {item.component === 'navigation_sub_link' ? (
                                            <>{getLinkFragment(item)}</>
                                        ) : (
                                            <button
                                                role="menuitem"
                                                id={navigationId + '-button-' + item._uid}
                                                className={activeNavigationItem === item._uid ? styles.navigationItemActive : ''}
                                                aria-haspopup="true"
                                                aria-controls={navigationId + '-' + item._uid}
                                                onClick={() => {
                                                    if (onNavigationItemChange) {
                                                        onNavigationItemChange(item);
                                                        setActiveLink(item._uid);
                                                    }
                                                }}
                                            >
                                                {item.text}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}
        </>
    );
};

export default NavigationItem;
